import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, TrendingUp } from 'lucide-react';
import { salesService } from '../../services/salesService';
import { useAuth } from '../../context/AuthContext';

const PLATFORMS = ['Upwork', 'Direct', 'Referral', 'Other'];

const NewLead = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Feature: Lead attribution - Sales Reps are always attributed to themselves;
  // Sales Managers/Admins must explicitly pick who brought in the lead.
  const needsRepPicker = user && user.role !== 'sales_rep';
  const [salesReps, setSalesReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(needsRepPicker);

  useEffect(() => {
    if (!needsRepPicker) return;
    const loadReps = async () => {
      try {
        const res = await salesService.getSalesReps();
        setSalesReps(res.data || []);
      } catch (err) {
        console.error('Failed to load sales reps:', err);
      } finally {
        setRepsLoading(false);
      }
    };
    loadReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formData = new FormData(e.target);

    if (needsRepPicker && !formData.get('sales_rep_id')) {
      setError('Please select the Sales Representative who brought in this lead.');
      setLoading(false);
      return;
    }

    const data = {
      client_name: formData.get('client_name'),
      platform: formData.get('platform'),
      service_required: formData.get('service_required'),
      estimated_value: parseFloat(formData.get('estimated_value')),
      first_contact_date: formData.get('first_contact_date'),
      contact_email: formData.get('contact_email') || null,
      contact_phone: formData.get('contact_phone') || null,
      notes: formData.get('notes') || null,
      sales_rep_id: needsRepPicker ? formData.get('sales_rep_id') : null
    };

    try {
      await salesService.createLead(data);
      navigate('/sales');
    } catch (err) {
      setError(err.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-20">
      <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm font-bold uppercase tracking-widest">
        <ArrowLeft className="w-4 h-4" /> Back to Pipeline
      </button>

      <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/30 flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-900 tracking-tight">Register New Lead</h1>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Start a new sales opportunity</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm font-medium">{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Client / Company Name</label>
              <input name="client_name" required className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white focus:ring-4 focus:ring-primary/5 outline-none transition-all" placeholder="e.g. Acme Corp" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Platform Source</label>
              <select name="platform" required className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white outline-none transition-all">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {needsRepPicker && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Sales Representative *</label>
              <select name="sales_rep_id" required disabled={repsLoading} className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white outline-none transition-all disabled:opacity-60">
                <option value="">{repsLoading ? 'Loading...' : 'Select who brought in this lead'}</option>
                {salesReps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Service Required</label>
            <input name="service_required" required className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white focus:ring-4 focus:ring-primary/5 outline-none transition-all" placeholder="e.g. Web Development, UI/UX Design" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Estimated Deal Value</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">$</span>
                <input name="estimated_value" type="number" step="0.01" required className="w-full p-3 pl-8 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white outline-none transition-all" placeholder="0.00" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">First Contact Date</label>
              <input name="first_contact_date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 focus:bg-white outline-none transition-all" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-50">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Email (Optional)</label>
              <input name="contact_email" type="email" className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 outline-none" placeholder="client@example.com" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Phone (Optional)</label>
              <input name="contact_phone" className="w-full p-3 border border-gray-100 rounded-xl bg-gray-50 outline-none" placeholder="+1..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Internal Notes</label>
            <textarea name="notes" className="w-full p-4 border border-gray-100 rounded-2xl bg-gray-50 outline-none h-32 resize-none" placeholder="Any specific requirements or background info..." />
          </div>

          <div className="flex justify-end pt-4">
            <button disabled={loading} className="bg-primary text-white px-10 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Create Opportunity
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewLead;